import React from 'react';
import { Users, ClipboardList, BookOpen, CalendarCheck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import Badge from '../../components/common/Badge';
import { formatDate } from '../../utils/helpers';
function TeacherDashboard() {
  const { students, classes, exams, homework, announcements, attendance, academicYear } = useApp();
  const { currentUser } = useAuth();
  const myStaffId = currentUser?.profileId;
  const myClasses = classes.filter(c=>c.classTeacherId===myStaffId);
  const myStudents = students.filter(s=>myClasses.some(c=>c.id===s.classId)&&s.status==='active');
  const myHomework = homework.filter(h=>h.assignedBy===myStaffId&&h.status==='active');
  const today = new Date().toISOString().split('T')[0];

  const myTodayAttendance = myClasses.map(cls=>{
    const recs = attendance.filter(a=>a.date===today&&a.classId===cls.id&&a.entityType==='student');
    const present = recs.filter(a=>a.status==='present').length;
    const total = students.filter(s=>s.classId===cls.id&&s.status==='active').length;
    return { cls, present, total };
  });

  const upcomingExams = exams.filter(e=>e.status==='scheduled'||e.status==='ongoing').slice(0,3);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Teacher Dashboard</h1>
        <p className="text-gray-500 text-sm mt-0.5">Academic Year {academicYear} &mdash; Welcome, {currentUser?.name}!</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="My Students" value={myStudents.length} subtext="Across my classes" colorClass="bg-purple-500" />
        <StatCard icon={BookOpen} label="My Classes" value={myClasses.length} subtext="Assigned" colorClass="bg-blue-500" />
        <StatCard icon={ClipboardList} label="Active Homework" value={myHomework.length} subtext="Pending assignments" colorClass="bg-orange-500" />
        <StatCard icon={CalendarCheck} label="Upcoming Exams" value={upcomingExams.length} subtext="Scheduled" colorClass="bg-purple-500" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold mb-3">Today's Attendance</h3>
        {myTodayAttendance.length===0 ? <p className="text-gray-400 text-sm">No classes assigned</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {myTodayAttendance.map(({cls,present,total})=>(
              <div key={cls.id} className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium">{cls.name} - {cls.section}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width:(total>0?Math.round(present/total*100):0)+'%'}}></div>
                  </div>
                  <span className="">{present}/{total}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Upcoming Exams</h3>
          <div className="space-y-2">
            {upcomingExams.map(exam=>(
              <div key={exam.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="font-medium text-sm">{exam.name}</p>
                  <p className="">{formatDate(exam.startDate)} - {formatDate(exam.endDate)}</p>
                </div>
                <Badge value={exam.status} />
              </div>
            ))}
            {upcomingExams.length===0 && <p className="text-gray-400 text-sm">No upcoming exams</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">My Active Homework</h3>
          <div className="space-y-2">
            {myHomework.map(hw=>(
              <div key={hw.id} className="py-2 border-b border-gray-50 last:border-0">
                <p className="font-medium text-sm">{hw.title}</p>
                <p className="text-red-500 font-medium">Due: {formatDate(hw.dueDate)}</p>
              </div>
            ))}
            {myHomework.length===0 && <p className="text-gray-400 text-sm">No active homework assigned</p>}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold mb-3">Announcements for Teachers</h3>
        <div className="space-y-2">
          {announcements.filter(a=>a.isActive&&(!a.targetRoles||a.targetRoles.includes('teacher')||a.targetRoles.includes('all'))).slice(0,3).map(ann=>(
            <div key={ann.id} className="py-2 border-b border-gray-50 last:border-0">
              <p className="font-medium text-sm">{ann.title}</p>
              <p className="">{ann.content.slice(0,80)}...</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default TeacherDashboard;
